import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { AppIcon } from '../icons/AppIcon'
import { useCreditBalance } from '../../hooks/credits/useCreditBalance'
import { useOnboardingCelebration } from '../../store/onboardingCelebrationStore'
import { BalanceSourceBreakdown } from './BalanceSourceBreakdown'
import { ExpiringNextNotice } from './ExpiringNextNotice'

export function BalanceBadge() {
  const { t } = useTranslation('credits')
  const { data: balance, isLoading } = useCreditBalance()
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const pulseKey = useOnboardingCelebration((s) => s.pulseKey)
  const pointsAdded = useOnboardingCelebration((s) => s.pointsAdded)
  const [pulsing, setPulsing] = useState(false)

  useEffect(() => {
    if (!pulseKey) return
    setPulsing(true)
    const timer = window.setTimeout(() => setPulsing(false), 2500)
    return () => window.clearTimeout(timer)
  }, [pulseKey])

  if (isLoading) {
    return (
      <div className="flex items-center gap-1 text-sm text-gray-400">
        <AppIcon name="coins" size={16} className="text-gray-400" />
        <span>…</span>
      </div>
    )
  }

  const total = balance?.total ?? 0
  const buckets = balance?.buckets ?? []
  const hasSubscription = Boolean(balance?.subscription)
  const expiringNext = balance?.expiring_next

  const LOW_BALANCE_THRESHOLD = 5
  const isLow = total > 0 && total <= LOW_BALANCE_THRESHOLD
  const isEmpty = total === 0

  return (
    <div className="relative inline-flex items-center">
      <button
        type="button"
        onClick={() => setTooltipOpen((v) => !v)}
        className={`flex items-center gap-1.5 text-sm font-medium rounded px-2 py-1 transition-colors ${
          pulsing
            ? 'text-green-300 bg-green-950/50 ring-2 ring-green-400/60 animate-pulse'
            : isEmpty
            ? 'text-red-400 bg-red-950/40 hover:bg-red-950/60'
            : isLow
              ? 'text-amber-400 bg-amber-950/40 hover:bg-amber-950/60'
              : 'text-gray-300 hover:text-white hover:bg-[#1f1f1f]'
        }`}
        aria-label={t('balance.ariaLabel')}
      >
        <AppIcon
          name="coins"
          size={16}
          className={isEmpty ? 'text-red-400' : isLow ? 'text-amber-400' : 'text-yellow-400'}
        />
        <span className="text-current">{total.toLocaleString()} {t('unit')}</span>
        {(isEmpty || isLow) && (
          <span className="text-xs opacity-75">{isEmpty ? t('balance.empty') : t('balance.low')}</span>
        )}
      </button>

      {tooltipOpen && (
        <div
          className="absolute top-full right-0 mt-1 z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3"
          onMouseLeave={() => setTooltipOpen(false)}
        >
          <div className="text-xs font-semibold text-gray-500 mb-2">
            {t('balance.breakdown.title')}
          </div>
          <BalanceSourceBreakdown
            buckets={buckets}
            variant="rows"
            emptyLabel={t('balance.breakdown.empty')}
          />
          {pulsing && pointsAdded > 0 && (
            <div className="mt-2 rounded-lg bg-green-50 px-2 py-1.5 text-xs font-semibold text-green-700">
              +{pointsAdded} {t('unit')}
            </div>
          )}
          {expiringNext && expiringNext.points > 0 ? (
            <ExpiringNextNotice expiring={expiringNext} variant="badge" className="mt-2" />
          ) : null}
          {(isEmpty || isLow) && !hasSubscription && (
            <p className="mt-2 text-xs text-blue-700 leading-snug">
              {t('balance.subscriptionHint')}
            </p>
          )}
          <Link
            to="/credits"
            className="mt-2 block text-xs text-center text-blue-600 hover:underline"
            onClick={() => setTooltipOpen(false)}
          >
            {isEmpty && !hasSubscription
              ? t('balance.subscribeCta')
              : t('balance.breakdown.historyLink')}
          </Link>
        </div>
      )}
    </div>
  )
}
